import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch items
export const fetchItems = createAsyncThunk(
  'item/fetchItems',
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching items with page:', page, 'and limit:', limit);
      const response = await api.items.getAll({ page, limit });
      if (response.data.status) {
        log.info('Items fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch items:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching items:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching items');
    }
  }
);

// Search items with keyword
export const searchItems = createAsyncThunk(
  'item/searchItems',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching items with keyword:', keyword);
      const response = await api.items.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Items search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search items:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching items:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching items');
    }
  }
);

// Create item
export const createItem = createAsyncThunk(
  'item/createItem',
  async (itemData, { rejectWithValue }) => {
    try {
      log.info('Creating item with data:', itemData);
      const response = await api.items.create(itemData);
      if (response.data.status) {
        log.info('Item created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create item:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating item:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create item');
    }
  }
);

// Update item
export const updateItem = createAsyncThunk(
  'item/updateItem',
  async ({ id, itemData }, { rejectWithValue }) => {
    try {
      log.info('Updating item with ID:', id);
      const response = await api.items.update(id, itemData);
      if (response.data.status) {
        log.info('Item updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update item:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating item:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating item');
    }
  }
);

// Delete item
export const deleteItem = createAsyncThunk(
  'item/deleteItem',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting item with ID:', id);
      const response = await api.items.delete(id);
      if (response.data.status) {
        log.info('Item deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete item:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting item:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting item');
    }
  }
);

// Bulk delete items
export const bulkDeleteItems = createAsyncThunk(
  'item/bulkDeleteItems',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.items.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting items');
    }
  }
);

// Bulk activate/deactivate items
export const bulkUpdateItemStatus = createAsyncThunk(
  'item/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.items.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating item status');
    }
  }
);

// Fetch a single item by ID
export const fetchItemById = createAsyncThunk(
  'item/fetchItemById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching item with ID:', id);
      const response = await api.items.getById(id);
      if (response.data.status) {
        log.info('Item fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch item:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching item:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching item');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const itemSlice = createSlice({
  name: 'item',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch items
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search items
      .addCase(searchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.items || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create item
      .addCase(createItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItem.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update item
      .addCase(updateItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete item
      .addCase(deleteItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteItem.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete items
      .addCase(bulkDeleteItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteItems.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteItems.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateItemStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateItemStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateItemStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch a single item by ID
      .addCase(fetchItemById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          state.items.push(action.payload);
        }
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default itemSlice.reducer; 